import type { MouseEvent } from "react";
import React from "react";
import Image from "next/image";
import { addMembershipById } from "lib/api";
import { useSegment } from "tracking";
import { useUserV2 } from "hooks";
import type { PLPMidPageBannerType } from "@shared/graphql.types";
import {
  wrapper,
  imageButton,
  imageContainer,
  desktopImageContainer,
  mobileImageContainer
} from "./MidPageBanner.css";

type Props = {
  banners: (PLPMidPageBannerType | undefined)[];
  onOpen?: () => void;
};

export const MidPageBanner: React.FC<Props> = ({ banners, onOpen }) => {
  const user = useUserV2();
  const { track } = useSegment();

  const canShowBanner =
    (!user.hasMembershipInBag && !user.isLoggedIn) || user.isLoggedIn;

  const bannerData = banners?.[0];
  if (!bannerData || !canShowBanner) return null;

  const onClick = (e: MouseEvent) => {
    e.preventDefault();
    if (!membershipId) return;
    addMembershipById(membershipId)
      .then(() => {
        if (onOpen) onOpen();
      })
      .catch(() =>
        console.error(
          `Could not add membership:${membershipId} to bag in MembershipCard`
        )
      );

    trackClick();
  };

  const trackClick = () => {
    track({
      event: "Page Element Clicked",
      properties: {
        module_content: bannerData?.title ?? ""
      }
    });
  };

  const { desktopImage, mobileImage, href, membershipId } = bannerData;

  const imageData = [
    {
      isMobile: false,
      src: desktopImage?.src,
      alt: desktopImage?.description ?? ""
    },
    {
      isMobile: true,
      src: mobileImage?.src,
      alt: mobileImage?.description ?? ""
    }
  ];

  const body = (
    <>
      {imageData.map(({ src, alt, isMobile }) => {
        return (
          src && (
            <div
              className={`${imageContainer} ${
                isMobile ? mobileImageContainer : desktopImageContainer
              }`}
              key={src}
            >
              <Image
                src={src}
                alt={alt}
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 36.75rem"
                style={{
                  objectFit: "cover"
                }}
              />
            </div>
          )
        );
      })}
    </>
  );

  return (
    <div className={wrapper} data-testid="marketing-tile">
      {membershipId ? (
        <button type="button" className={imageButton} onClick={onClick}>
          {body}
        </button>
      ) : (
        <a href={href as string} onClick={trackClick}>
          {body}
        </a>
      )}
    </div>
  );
};
